import {signalStore, withState, withMethods, withProps, patchState} from '@ngrx/signals';
import {inject} from '@angular/core';
import {SessionState} from './session-state';
import {
  BasicTaDetails, BasicUserDetails,
  FileDetails, FileDetailsRestResponse,
  FullTaDetails,
  ItineraryItemDetails, LocationOption, ProjectDetails,
  SessionDetails, SubmissionExpenseResult, SubmissionSearchResult,
  TAStatus,
  TravellerDetails,
  UserDetails, UserEvent,
  UserTravelItinerary
} from '../api';
import {environment} from '../environments/environment';
import {
  ACTIVE_TA_STATUSES,
  BreadCrumbData,
  CLOSED_TA_STATUSES,
  CreateTaStep,
  CreateTaStepType,
  DelegateTaMode,
  UnitTaMode,
  FinanceSearchState,
  SearchTaList
} from '../util/app.types';
import {reconcileCachedTa} from '../util/ta-list-cache.utils';
import {QueueService} from '../services/queue.service';
import {CreateTaDetailsComponent} from '../app/components/create-ta/create-ta-details/create-ta-details.component';

export type ClcTaAppState = {
  session: SessionState;
  user: UserDetails | null;
  showEnvName: boolean;
  taSubmission: FullTaDetails | undefined;
  currentItinerary: UserTravelItinerary | undefined;
  taDocuments: FileDetails[];
  taValue: SubmissionExpenseResult | undefined;
  currentCreateTaStep: CreateTaStep;
  createTaSteps: CreateTaStep[];
  availableUsers: UserDetails[];
  userSearchOptions: BasicUserDetails[];
  availableProjects: ProjectDetails[];
  availableLocations: LocationOption[];
  availablePrimaryApprovers: UserDetails[];
  historyTaListCache: SearchTaList | undefined;
  approvalHistoryTaListCache: SearchTaList | undefined;
  directReportTaListCache: SearchTaList | undefined;
  directReportHistoryTaListCache: SearchTaList | undefined;
  unitActiveTaListCache: SearchTaList | undefined;
  unitClosedTaListCache: SearchTaList | undefined;
  delegateActiveTaListCache: SearchTaList | undefined;
  delegateClosedTaListCache: SearchTaList | undefined;
  // Per-section loading flags: both delegate sections fetch page 0 in the same tick, so the
  // shared isLoadingData flag would be switched off by whichever response lands first.
  delegateActiveLoading: boolean;
  delegateClosedLoading: boolean;
  locationsLoading: boolean;
  financeSearchState: FinanceSearchState;
  dontShowCreateTaDialog: boolean;
  breadCrumbData: BreadCrumbData
  isLoadingData: boolean;
  previousPageRoute: string;
}

function _getEnvName(): string {
  try {
    const url = new URL(environment.tenantSettings.baseUrl);
    const host = url.host;
    if (host === 'localhost') {
      return 'LOCAL DEV';
    }
    if (host.includes('dev')) {
      return 'DEV ENVIRONMENT';
    }
  } catch(error) {
    // baseUrl is not a valid URL — skip environment name detection
    console.log(' error getting environment url',error);
  }
  return '';
}

const baseCreateTaSteps: Record<CreateTaStepType, CreateTaStep> =  {
  [CreateTaStepType.TRAVELLERS]: {
    title: "Who's Travelling",
    subtitle: "Who's Travelling on this TA",
    code: CreateTaStepType.TRAVELLERS,
    component: CreateTaDetailsComponent,
    enabled: true,
    valid: false,
  },
  [CreateTaStepType.DETAILS]: {
    title: "Travel Details",
    code: CreateTaStepType.DETAILS,
    component: CreateTaDetailsComponent,
    enabled: true,
    valid: false,
  },
  [CreateTaStepType.ITINERARY]: {
    title: "Itinerary",
    subtitle: "Your Travel Itinerary",
    code: CreateTaStepType.ITINERARY,
    component: CreateTaDetailsComponent,
    enabled: true,
    valid: false,
  },
  [CreateTaStepType.DOCUMENTATION]: {
    title: "Documentation",
    subtitle: "Travel Documentation",
    code: CreateTaStepType.DOCUMENTATION,
    component: CreateTaDetailsComponent,
    enabled: true,
    valid: false,
  },
  [CreateTaStepType.REVIEW]: {
    title: "Review",
    code: CreateTaStepType.REVIEW,
    component: null,
    enabled: true,
    valid: false,
  },
};

const createTaSteps: CreateTaStep[] = [
    baseCreateTaSteps[CreateTaStepType.TRAVELLERS],
    baseCreateTaSteps[CreateTaStepType.DETAILS],
    baseCreateTaSteps[CreateTaStepType.ITINERARY],
    baseCreateTaSteps[CreateTaStepType.DOCUMENTATION],
    baseCreateTaSteps[CreateTaStepType.REVIEW],
];

const initbreadCrumbData: BreadCrumbData = {
  title: '',
  titleIcon:'',
  section: '',
  breadTrail: [],
  taNumberPrefix: undefined,
  showSaveStatus: false,
  showTaComments: false,
};

const initialState: ClcTaAppState = {
  session: {details: null, environmentName: _getEnvName()},
  user: null,
  showEnvName: true,
  taSubmission: undefined,
  currentItinerary: undefined,
  taDocuments: [],
  taValue: undefined,
  currentCreateTaStep: baseCreateTaSteps[CreateTaStepType.TRAVELLERS],
  createTaSteps: createTaSteps,
  availableUsers: [],
  userSearchOptions: [],
  availableProjects: [],
  availableLocations: [],
  availablePrimaryApprovers: [],
  historyTaListCache: undefined,
  approvalHistoryTaListCache: undefined,
  directReportTaListCache: undefined,
  directReportHistoryTaListCache: undefined,
  unitActiveTaListCache: undefined,
  unitClosedTaListCache: undefined,
  delegateActiveTaListCache: undefined,
  delegateClosedTaListCache: undefined,
  delegateActiveLoading: false,
  delegateClosedLoading: false,
  financeSearchState: {
    advancedEnabled: false,
    basicSearchStr: '',
    advancedSearchParameters: {
      taStatusList: [],
      onlyPrimaryTraveller: false,
      projectList: [],
      travellerList: [],
      approverList: [],
      createdByList: [],
      dateStart: undefined,
      dateEnd: undefined,
    },
    currentPage: 0,
    results: undefined,
    hasSearched: false,
  },
  locationsLoading: false,
  dontShowCreateTaDialog: localStorage['taInfoModalDontShowSetting'] === 'true',
  breadCrumbData: initbreadCrumbData,
  isLoadingData: false,
  previousPageRoute: '/home',
}

// Patches a changed TA into every cached list already holding it. Pure, so
// updateTaSubmissionPartial can fold it into its own patchState call.
function reconcileListCaches(
  state: ClcTaAppState,
  changed: Partial<BasicTaDetails>,
): Partial<ClcTaAppState> {
  if (!changed.id || changed.status === undefined || changed.status === null) {
    return {};
  }
  const target = changed as BasicTaDetails;

  // SearchSubmissions excludes Deleted for every caller, so a deleted TA leaves every list.
  const isDeleted = target.status === TAStatus.Deleted;
  const activeNow = !isDeleted && ACTIVE_TA_STATUSES.includes(target.status!);
  const closedNow = !isDeleted && CLOSED_TA_STATUSES.includes(target.status!);

  const active = reconcileCachedTa(state.delegateActiveTaListCache, target, activeNow);
  const closed = reconcileCachedTa(state.delegateClosedTaListCache, target, closedNow);

  // The three other active/closed pairs: direct reports, and the unit manager lists.
  const reportsActive = reconcileCachedTa(state.directReportTaListCache, target, activeNow);
  const reportsClosed = reconcileCachedTa(state.directReportHistoryTaListCache, target, closedNow);
  const unitActive = reconcileCachedTa(state.unitActiveTaListCache, target, activeNow);
  const unitClosed = reconcileCachedTa(state.unitClosedTaListCache, target, closedNow);

  // Search results can carry a status filter, so strictly a status change can drop a row. Patched
  // rather than removed anyway: a row vanishing mid-read after the user's own action is worse.
  const searchResults = reconcileCachedTa(state.financeSearchState.results, target, !isDeleted);

  return {
    // Neither /user/taHistory nor /user/approval-history filters on status, so a row leaves these
    // two only by being deleted.
    historyTaListCache: reconcileCachedTa(state.historyTaListCache, target, !isDeleted).cache,
    approvalHistoryTaListCache:
      reconcileCachedTa(state.approvalHistoryTaListCache, target, !isDeleted).cache,
    // Leaving one half of an active/closed pair means the row belongs in the other; invalidate that
    // one so the next open refetches it in the server's sort order instead of inserting at a guessed
    // position.
    delegateActiveTaListCache: closed.removed && !isDeleted ? undefined : active.cache,
    delegateClosedTaListCache: active.removed && !isDeleted ? undefined : closed.cache,
    directReportTaListCache: reportsClosed.removed && !isDeleted ? undefined : reportsActive.cache,
    directReportHistoryTaListCache:
      reportsActive.removed && !isDeleted ? undefined : reportsClosed.cache,
    unitActiveTaListCache: unitClosed.removed && !isDeleted ? undefined : unitActive.cache,
    unitClosedTaListCache: unitActive.removed && !isDeleted ? undefined : unitClosed.cache,
    // Only rebuilt when the results changed - it is nested, so churning it would recompute every
    // search-tab binding on any TA action.
    ...(searchResults.cache !== state.financeSearchState.results
      ? {financeSearchState: {...state.financeSearchState, results: searchResults.cache}}
      : {}),
  };
}

//signalStore = REDUX
export const TaAppStore = signalStore(
  {providedIn: 'root'},
  withState(initialState),
  // Read from QueueService rather than mirrored into state: the queue owns the count, and a copy in
  // state could only ever drift from it.
  withProps(() => ({
    /**
     * True while a queued save is outstanding. Components gate destructive/reload actions on it,
     * and bread-crumb-bar renders it as the "Saving..." indicator.
     */
    isSavingData: inject(QueueService).isSaving,
  })),
  withMethods((store) => ({
    updateSession(details: SessionDetails): void {
      patchState( store, (state) => ({session: {...state.session, details: details}, user: details.user}));
    },
    updateVersion(aVersion: string): void {
      patchState(store, (state) => ({
        session: {
          ...state.session,
          details: state.session.details ? {
            ...state.session.details,
            version: aVersion
          } : null
        }
      }));
    },
    getTaSubmission(): FullTaDetails {
      let current = store.taSubmission();
      if (!current) {
        throw new Error('TA Submission not found.');
      }
      return current;
    },
    updateTaSubmissionField(key: string, value: any): void {
      patchState( store, (state) => ({
        taSubmission: {...state.taSubmission, [key]: value}
      }));
    },
    // Every lifecycle mutation in TaSubmissionDatasource ends here with the server's fresh
    // BasicTaDetails, so it is also where the cached TA lists get written through.
    updateTaSubmissionPartial(update: Partial<FullTaDetails>) {
      patchState( store, (state) => ({
        taSubmission: state.taSubmission ? {...state.taSubmission, ...update} : undefined,
        ...reconcileListCaches(state, update),
      }));
    },
    updateTaSubmission(taSub: FullTaDetails | undefined): void {
      patchState( store, (state) => ({
        taSubmission: taSub,
      }));
    },
    updateBreadCrumbData(breadCrumbData: Partial<BreadCrumbData>): void {
      patchState( store, (state) => ({
        breadCrumbData: {
          ...state.breadCrumbData,
          taNumberPrefix: undefined,
          // Opt-in per page, so a TA page's prefix and save indicator are never inherited by the
          // next screen - both are cleared here and re-set only by the pages that want them.
          showSaveStatus: false,
          // Opt-in per page, so a TA page's prefix and comments button are never inherited by the
          // next screen.
          showTaComments: false,
          templateMode: false,
          ...breadCrumbData,
        }
      }));
    },
    updateCurrentCreateTaStep(createTaStepType: CreateTaStepType): void {
      patchState( store, (state) => {
        let currentStep = state.createTaSteps.find(step => step.code === createTaStepType);
        return ({
          currentCreateTaStep: currentStep,
        })
      })
    },
    incrementCreateTaStep(): void {
      patchState( store, (state) => {
        const currentIndex = state.createTaSteps.indexOf(state.currentCreateTaStep);
        if (currentIndex < createTaSteps.length - 1) {
          return { currentCreateTaStep: createTaSteps[currentIndex + 1] };
        }
        return state;
      })
    },
    decrementCreateTaStep(): void {
      patchState( store, (state) => {
        const currentIndex = state.createTaSteps.indexOf(state.currentCreateTaStep);
        if (currentIndex > 0 ) {
          return { currentCreateTaStep: createTaSteps[currentIndex - 1] };
        }
        return state;
      });
    },
    updateCreateTaSteps(createTaSteps: CreateTaStep[]): void {
      patchState( store, (state) => ({
        createTaSteps: [...createTaSteps],
      }));
    },
    updateCreateTaStepValidator(createTaStep: CreateTaStepType, enabled: boolean, valid: boolean): void {
      patchState( store, (state) => {
        let newSteps = state.createTaSteps.map(step => {
          if (createTaStep === step.code) {
            return {
              ...step,
              enabled: enabled,
              valid: valid,
            }
          }
          return step;
        });
        return {createTaSteps: [...newSteps]};
      });
    },
    getUserItinerary(): UserTravelItinerary {
      let itinerary = store.currentItinerary();
      if (!itinerary) {
        throw new Error('User itinerary not found.');
      }
      return itinerary;
    },
    updateCurrentItinerary(itinerary: UserTravelItinerary | undefined): void {
      if (!itinerary) {
        patchState( store, (state) => ({
           currentItinerary: undefined
        }));
      } else {
        patchState( store, (state) => ({
          currentItinerary: {
            ...itinerary,
            items: [...itinerary?.items ?? []].sort((a,b) => {
              const aStart = a.start ? Date.parse(a.start) : Infinity;
              const bStart = b.start ? Date.parse(b.start) : Infinity;
              return aStart - bStart;
            }),
          },
        }));
      }
    },
    addCurrentItineraryItem(itineraryItem: ItineraryItemDetails) {
      patchState( store, (state) => {
        let currentItineraryNew: UserTravelItinerary = {
            ...state.currentItinerary,
            items: [...(state.currentItinerary?.items ?? []), itineraryItem].sort((a,b) => {
              const aStart = a.start ? Date.parse(a.start) : Infinity;
              const bStart = b.start ? Date.parse(b.start) : Infinity;
              return aStart - bStart;
            }),
        }
        return {
          currentItinerary: currentItineraryNew,
        }
      });
    },
    updateCurrentItineraryItem(itineraryItem: ItineraryItemDetails): void {
      patchState( store, (state) => {
        let currentItineraryNew: UserTravelItinerary = {
          ...state.currentItinerary,
          items: state.currentItinerary?.items ? [
              ...state.currentItinerary.items.filter(item => item.id !== itineraryItem.id),
              itineraryItem
            ].sort((a,b) => {
              const aStart = a.start ? Date.parse(a.start) : Infinity;
              const bStart = b.start ? Date.parse(b.start) : Infinity;
              return aStart - bStart;
            })
            : []
        }
        return {
          currentItinerary: currentItineraryNew,
        }
      });
    },
    /**
     * Merge only the server-computed allowance amounts onto the current itinerary's items, matched
     * by id. For the meal-claim save, which keeps its own optimistic mealClaims - adopting the
     * server item wholesale is what made the checkboxes flicker - but still has to pick up the
     * recalculated dollar values, which are server-computed and rendered straight off the item.
     * Outside manual mode the rule-owned incidentals/dayTrip ticks are taken from the server too.
     */
    updateCurrentItineraryItemValues(serverItems: ItineraryItemDetails[]): void {
      patchState( store, (state) => {
        const serverById = new Map(serverItems.filter(i => !!i.id).map(i => [i.id, i] as const));
        const isManual = !!state.currentItinerary?.isManual;
        let currentItineraryNew: UserTravelItinerary = {
          ...state.currentItinerary,
          items: state.currentItinerary?.items?.map(item => {
            const server = serverById.get(item.id);
            return !server ? item : {
              ...item,
              // Outside manual mode incidentals and day trip are the server rules' ticks, not the user's
              mealClaims: isManual ? item.mealClaims : {
                ...item.mealClaims,
                incidentals: !!server.mealClaims?.['incidentals'],
                dayTrip: !!server.mealClaims?.['dayTrip'],
              },
              breakfastValue: server.breakfastValue,
              lunchValue: server.lunchValue,
              dinnerValue: server.dinnerValue,
              incidentalValue: server.incidentalValue,
              cateringValue: server.cateringValue,
              dayTripValue: server.dayTripValue,
              overseasValue: server.overseasValue,
            };
          }) ?? []
        }
        return {
          currentItinerary: currentItineraryNew,
        }
      });
    },
    removeCurrentItineraryItem(id: string): void {
      patchState( store, (state) => {
        let currentItineraryNew: UserTravelItinerary = {
          ...state.currentItinerary,
          items: state.currentItinerary?.items?.filter(item => item.id !== id) ?? []
        }
        return {
          currentItinerary: currentItineraryNew,
        }
      });
    },
    setFileDetails(fileDetailsArray: FileDetails[]) {
      patchState( store, (state) => ({
        taDocuments: [...fileDetailsArray],
      }));
    },
    addFileDetails(fileDetails: FileDetails): void {
      patchState( store, (state) => ({
        taDocuments: [...state.taDocuments, fileDetails],
      }));
    },
    updateFileDetails(fileDetails: FileDetails): void {
      patchState( store, (state) => ({
        taDocuments: [...state.taDocuments.filter(item => item.fileId !== fileDetails.fileId), fileDetails],
      }));
    },
    removeFileDetails(filedId: string): void {
      patchState( store, (state) => ({
        taDocuments: state.taDocuments.filter(item => item.fileId !== filedId),
      }));
    },
    updateTaValue(expenses: SubmissionExpenseResult): void {
      patchState( store, (state) => {
        // Estimate responses can land after the user has moved to another TA. Dropping a
        // mismatched result stops one TA's values being shown against another's.
        const currentId = state.taSubmission?.id;
        if (!!expenses.submissionId && !!currentId && expenses.submissionId !== currentId) {
          return {};
        }
        return {taValue: expenses};
      });
    },
    updateDontShowCreateTaDialog(option: boolean): void {
      patchState( store, (state) => ({
        dontShowCreateTaDialog: option,
      }));
    },
    updateAvailableUsers(users: UserDetails[]) {
      patchState( store, (state) => ({
        availableUsers: [...users],
      }));
    },
    updateUserSearchOptions(users: BasicUserDetails[]) {
      patchState( store, (state) => ({
        userSearchOptions: [...users],
      }));
    },
    updateAvailableProjects(projects: ProjectDetails[]) {
      patchState( store, (state) => ({
        availableProjects: [...projects],
      }));
    },
    updateAvailableLocations(locations: LocationOption[]) {
      patchState( store, (state) => ({
        availableLocations: [...locations],
      }));
    },
    updateAvailablePrimaryApprovers(users: UserDetails[]) {
      patchState( store, () => ({
        availablePrimaryApprovers: [...users],
      }));
    },
    updateHistoryTaListCache(historyTaList: SubmissionSearchResult) {
      patchState( store, (state) => ({
        historyTaListCache: {
          totalCount: historyTaList.totalCount,
          taList: historyTaList.results ?? [],
          currentCount: historyTaList.results?.length ?? 0,
          loadedAt: Date.now(),
          pagesLoaded: 1,
        },
      }));
    },
    resetHistoryCache(): void {
      patchState( store, () => ({
        historyTaListCache: undefined,
      }));
    },
    addExtraHistoryTas(newTasList: BasicTaDetails[]): void {
      patchState( store, (state) => ({
        historyTaListCache: {
          ...state.historyTaListCache,
          taList: [...state.historyTaListCache?.taList ?? [], ...newTasList],
          currentCount: (state.historyTaListCache?.currentCount ?? 0) + newTasList.length,
          pagesLoaded: (state.historyTaListCache?.pagesLoaded ?? 0) + 1,
        },
      }));
    },
    updateApprovalHistoryTaListCache(result: SubmissionSearchResult) {
      patchState( store, () => ({
        approvalHistoryTaListCache: {
          totalCount: result.totalCount,
          taList: result.results ?? [],
          currentCount: result.results?.length ?? 0,
          loadedAt: Date.now(),
          pagesLoaded: 1,
        },
      }));
    },
    addExtraApprovalHistoryTas(newTasList: BasicTaDetails[]): void {
      patchState( store, (state) => ({
        approvalHistoryTaListCache: {
          ...state.approvalHistoryTaListCache,
          taList: [...state.approvalHistoryTaListCache?.taList ?? [], ...newTasList],
          currentCount: (state.approvalHistoryTaListCache?.currentCount ?? 0) + newTasList.length,
          pagesLoaded: (state.approvalHistoryTaListCache?.pagesLoaded ?? 0) + 1,
        },
      }));
    },
    resetApprovalHistoryCache(): void {
      patchState( store, () => ({
        approvalHistoryTaListCache: undefined,
      }));
    },
    updateDirectReportTaListCache(result: SubmissionSearchResult) {
      patchState(store, () => ({
        directReportTaListCache: {
          totalCount: result.totalCount,
          taList: result.results ?? [],
          currentCount: result.results?.length ?? 0,
          loadedAt: Date.now(),
          pagesLoaded: 1,
        },
      }));
    },
    addExtraDirectReportTas(newTasList: BasicTaDetails[]): void {
      patchState(store, (state) => ({
        directReportTaListCache: {
          ...state.directReportTaListCache,
          taList: [...state.directReportTaListCache?.taList ?? [], ...newTasList],
          currentCount: (state.directReportTaListCache?.currentCount ?? 0) + newTasList.length,
          pagesLoaded: (state.directReportTaListCache?.pagesLoaded ?? 0) + 1,
        },
      }));
    },
    resetDirectReportCache(): void {
      patchState(store, () => ({directReportTaListCache: undefined}));
    },
    updateDirectReportHistoryTaListCache(result: SubmissionSearchResult) {
      patchState(store, () => ({
        directReportHistoryTaListCache: {
          totalCount: result.totalCount,
          taList: result.results ?? [],
          currentCount: result.results?.length ?? 0,
          loadedAt: Date.now(),
          pagesLoaded: 1,
        },
      }));
    },
    addExtraDirectReportHistoryTas(newTasList: BasicTaDetails[]): void {
      patchState(store, (state) => ({
        directReportHistoryTaListCache: {
          ...state.directReportHistoryTaListCache,
          taList: [...state.directReportHistoryTaListCache?.taList ?? [], ...newTasList],
          currentCount: (state.directReportHistoryTaListCache?.currentCount ?? 0) + newTasList.length,
          pagesLoaded: (state.directReportHistoryTaListCache?.pagesLoaded ?? 0) + 1,
        },
      }));
    },
    resetDirectReportHistoryCache(): void {
      patchState(store, () => ({directReportHistoryTaListCache: undefined}));
    },
    // The unit manager lists follow the delegate pattern: one pair of caches keyed by mode, so the
    // list component can be written once and used for both tabs.
    updateUnitTaListCache(mode: UnitTaMode, result: SubmissionSearchResult): void {
      const value: SearchTaList = {
        totalCount: result.totalCount,
        taList: result.results ?? [],
        currentCount: result.results?.length ?? 0,
        loadedAt: Date.now(),
        pagesLoaded: 1,
      };
      patchState(store, (): Partial<ClcTaAppState> => mode === 'active'
        ? {unitActiveTaListCache: value}
        : {unitClosedTaListCache: value});
    },
    addExtraUnitTas(mode: UnitTaMode, newTasList: BasicTaDetails[]): void {
      patchState(store, (state): Partial<ClcTaAppState> => {
        const current = mode === 'active' ? state.unitActiveTaListCache : state.unitClosedTaListCache;
        const value: SearchTaList = {
          ...current,
          taList: [...current?.taList ?? [], ...newTasList],
          currentCount: (current?.currentCount ?? 0) + newTasList.length,
          pagesLoaded: (current?.pagesLoaded ?? 0) + 1,
        };
        return mode === 'active'
          ? {unitActiveTaListCache: value}
          : {unitClosedTaListCache: value};
      });
    },
    resetUnitCache(mode: UnitTaMode): void {
      patchState(store, (): Partial<ClcTaAppState> => mode === 'active'
        ? {unitActiveTaListCache: undefined}
        : {unitClosedTaListCache: undefined});
    },
    updateDelegateTaListCache(mode: DelegateTaMode, result: SubmissionSearchResult): void {
      const value: SearchTaList = {
        totalCount: result.totalCount,
        taList: result.results ?? [],
        currentCount: result.results?.length ?? 0,
        loadedAt: Date.now(),
        pagesLoaded: 1,
      };
      patchState(store, (): Partial<ClcTaAppState> => mode === 'active'
        ? {delegateActiveTaListCache: value}
        : {delegateClosedTaListCache: value});
    },
    addExtraDelegateTas(mode: DelegateTaMode, newTasList: BasicTaDetails[]): void {
      patchState(store, (state): Partial<ClcTaAppState> => {
        const current = mode === 'active' ? state.delegateActiveTaListCache : state.delegateClosedTaListCache;
        const value: SearchTaList = {
          ...current,
          taList: [...current?.taList ?? [], ...newTasList],
          currentCount: (current?.currentCount ?? 0) + newTasList.length,
          pagesLoaded: (current?.pagesLoaded ?? 0) + 1,
        };
        return mode === 'active'
          ? {delegateActiveTaListCache: value}
          : {delegateClosedTaListCache: value};
      });
    },
    resetDelegateCache(mode: DelegateTaMode): void {
      patchState(store, (): Partial<ClcTaAppState> => mode === 'active'
        ? {delegateActiveTaListCache: undefined}
        : {delegateClosedTaListCache: undefined});
    },
    updateDelegateLoading(mode: DelegateTaMode, isLoading: boolean): void {
      patchState(store, (): Partial<ClcTaAppState> => mode === 'active'
        ? {delegateActiveLoading: isLoading}
        : {delegateClosedLoading: isLoading});
    },
    updateFinanceSearchResults(financeSearchList: SubmissionSearchResult) {
      patchState( store, (state) => ({
        financeSearchState: {
          ...state.financeSearchState,
          results: {
            totalCount: financeSearchList.totalCount,
            taList: financeSearchList.results ?? [],
            currentCount: financeSearchList.results?.length ?? 0,
          },
          hasSearched: true,
        },
      }));
    },
    addExtraFinanceSearchResults(newTasList: BasicTaDetails[]): void {
      patchState( store, (state) => ({
        financeSearchState: {
          ...state.financeSearchState,
          results: {
            ...state.financeSearchState.results,
            taList: [...state.financeSearchState.results?.taList ?? [], ...newTasList],
            currentCount: (state.financeSearchState.results?.currentCount ?? 0) + newTasList.length,
          },
        },
      }));
    },
    updateFinanceSearchParams(params: Partial<FinanceSearchState>): void {
      patchState( store, (state) => ({
        financeSearchState: {
          ...state.financeSearchState,
          ...params,
        },
      }));
    },
    hideEnvName(): void {
      patchState(store, (state) => ({showEnvName: false}));
    },
    updatePreviousPageRoute(route: string): void {
      patchState(store, () => ({previousPageRoute: route}));
    },
    resetTaState(): void {
      patchState( store, (state) => ({
        taSubmission: undefined,
        taValue: undefined,
        currentItinerary: undefined,
        taDocuments: [],
        currentCreateTaStep: baseCreateTaSteps[CreateTaStepType.TRAVELLERS],
      }));
    },
    resetState(): void {
      patchState( store, (state) => ({
        taSubmission: undefined,
        taValue: undefined,
        currentItinerary: undefined,
        taDocuments: [],
        currentCreateTaStep: baseCreateTaSteps[CreateTaStepType.TRAVELLERS],
        availableUsers: [],
        userSearchOptions: [],
        availableProjects: [],
        availableLocations: [],
        availablePrimaryApprovers: [],
        historyTaListCache: undefined,
        approvalHistoryTaListCache: undefined,
        directReportTaListCache: undefined,
        directReportHistoryTaListCache: undefined,
        unitActiveTaListCache: undefined,
        unitClosedTaListCache: undefined,
        delegateActiveTaListCache: undefined,
        delegateClosedTaListCache: undefined,
        delegateActiveLoading: false,
        delegateClosedLoading: false,
        locationsLoading: false,
        financeSearchState: {
          advancedEnabled: false,
          basicSearchStr: '',
          advancedSearchParameters: {
            taStatusList: [],
            onlyPrimaryTraveller: false,
            projectList: [],
            travellerList: [],
            dateStart: undefined,
            dateEnd: undefined,
          },
          currentPage: 0,
          results: undefined,
          hasSearched: false,
        },
      }));
    },
    updateLoadingData(isLoading: boolean): void {
      patchState(store, (state) => ({isLoadingData: isLoading}));
    },
    updateLocationsLoading(isLoading: boolean): void {
      patchState(store, () => ({locationsLoading: isLoading}));
    },
    getProjectApprover(): UserDetails | null {
      const current = store.taSubmission();
      if (!!current){
        if (current.projectId == null){
          return null;
        }
        if (!!current.secondaryApprover){
          return current.secondaryApprover;
        }
      }

      return null;
    }
  }))
);

export type TaAppStore = InstanceType<typeof TaAppStore>

